/**
 * Flattens a V3 content blob back to a V2-shaped (legacy) view for consumers
 * that operate job-wide rather than per-form.
 *
 * Safe to call on V2/V1 content — returns it unchanged (same reference).
 *
 * Output shape:
 *   - fields         concat of all forms' fields (default form first, then by displayOrder)
 *   - rules          concat of all forms' rules
 *   - export.destinations  job-level destinations with per-form mappings merged
 *   - formRecognition      default form's recognition config
 *   - pipeline, headerMapping, identity, profiles  copied from top-level
 *   - schemaVersion  2
 *
 * Intended to be composed with migrateContentToV3 so any schema version becomes
 * a consistent legacy view:
 *
 *   const flat = flattenToLegacyContent(migrateContentToV3(rawContent));
 */
export function flattenToLegacyContent(c) {
    if (c.schemaVersion !== 3) {
        // V1/V2 — already legacy-shaped; return unchanged
        return c;
    }
    const v3 = c;
    // Order: default form first, then remaining forms by displayOrder ascending.
    const defaultForm = v3.forms.find(f => f.isDefault) ?? v3.forms[0];
    const otherForms = v3.forms
        .filter(f => f !== defaultForm)
        .sort((a, b) => a.displayOrder - b.displayOrder);
    const orderedForms = defaultForm ? [defaultForm, ...otherForms] : otherForms;
    // Union all fields across all forms.
    const fields = orderedForms.flatMap(f => f.fields);
    // Union all rules across all forms.
    const rules = orderedForms.flatMap(f => f.rules);
    // Rebuild destinations with mappings from all forms merged per destination.
    // First-wins dedupe by slotPath ?? targetColumn (default form first per orderedForms;
    // matches the key convention established in slot-fulfillment.ts and slot-conformance.ts).
    const destinations = v3.exportDestinations.map(dest => {
        const allMappings = orderedForms.flatMap(form => {
            const em = form.exportMappings.find(m => m.destinationId === dest.id);
            return em ? em.mappings : [];
        });
        const seen = new Set();
        const dedupedMappings = allMappings.filter(m => {
            const key = m.slotPath ?? m.targetColumn;
            if (seen.has(key))
                return false;
            seen.add(key);
            return true;
        });
        return { ...dest, mappings: dedupedMappings };
    });
    return {
        schemaVersion: 2,
        fields,
        rules,
        pipeline: v3.pipeline,
        headerMapping: v3.headerMapping,
        identity: v3.identity,
        profiles: v3.profiles,
        formRecognition: defaultForm?.recognition,
        export: { destinations },
        // P8 — carry the job-level constants node onto the legacy view so runtime
        // ctx sites (which read the flattened content) can populate them. Omit the
        // key entirely when absent so a no-constants flatten stays byte-identical.
        ...(v3.constants ? { constants: v3.constants } : {}),
    };
}
//# sourceMappingURL=flatten-legacy.js.map